/**
 * 句子一体化学习流（Sentence Study Flow）
 * ------------------------------------------------------------
 * 一条连续流程，不出现「第几关」：
 *   1 understand  先理解：英文 + 中文一起给，听标准英文
 *   2 recall      记忆闪卡：句子只显示约 3 秒，然后**隐藏**，凭记忆回答关键信息
 *   （听句选句暂未启用：TTS 在部分平板上不稳定，句子朗读功能后续再加）
 *   4 fill        挖空补全（1 处）
 *   5 order       打乱重排
 *   6 translate   中文翻英文
 *   7 transform   句型替换 / 改写（例：There is a door. → There are two doors.）
 *   8 apply       简单应用（用自己的话用这个句型说一句）
 *
 * 「3 秒后隐藏」是本流程的关键动作：recall 步骤先暴露 3000ms，
 * 然后由 UI 把句子替换成占位符，再让学生回忆填空——这是真正的记忆提取，不是抄写。
 *
 * 同样的词/句，判定与落库复用 grade + PracticeSession 的 summary 结构。
 */
import { grade } from './graders/index.js';
import { shuffle, sample, tokenize, nowISO, uid } from '../utils/index.js';
import { makeQuestion } from '../core/models.js';

/** 闪卡暴露时长（毫秒） */
export const RECALL_MS = 3000;

export const SENTENCE_STEPS = [
  { key: 'understand', name: '读懂句子', icon: '📖', kind: 'preview' },
  { key: 'recall', name: '记忆闪卡', icon: '⚡', kind: 'recall' },
  { key: 'fill', name: '补全句子', icon: '🕳️', kind: 'quiz' },
  { key: 'order', name: '单词重排', icon: '🧩', kind: 'quiz' },
  { key: 'translate', name: '中文译英文', icon: '✏️', kind: 'quiz' },
  { key: 'transform', name: '句型替换', icon: '🔁', kind: 'quiz' },
  { key: 'apply', name: '用一用', icon: '🎭', kind: 'quiz' },
];

const STEP_LEVEL = {
  fill: 3, order: 4, translate: 5, transform: 6, apply: 7, recall: 2,
};

/**
 * 核心语法点挖空规则：pattern 里提到的关键词优先挖。
 * 找不到就降级挖较长的实义词。
 */
const KEYWORD_RULES = [
  // There be 句型 → 挖 be 动词
  { test: /there\s+(is|are|was|were)/i, findBe: true },
  // What's ... 's ...? → 挖 What's
  { test: /what'?s/i, target: /what'?s/i },
  // She's / He's / It's
  { test: /^(she|he|it)'?s/i, target: /^(she|he|it)'?s$/i },
  // have / has → 挖 have/has
  { test: /\bhave|has\b/i, target: /^(have|has)$/i },
  // can → 挖情态动词本身
  { test: /\bcan\b/i, target: /^can$/i },
  // Let's → 挖 Let's
  { test: /^let'?s/i, target: /^let'?s$/i },
  // This is / These are → 挖 This / These
  { test: /^(this|these|that|those)\s+(is|are)/i, target: /^(this|these|that|those)$/i },
];

const STOP_FILL = new Set(['a','an','the','to','of','and','or','in','on','at','for','with','by','from','it','its','my','your','his','her','our','their','this','that','these','those','be','been','being','do','does','did','i','you','he','she','we','they']);

/**
 * 智能挖空：先看 pattern 和句子里的语法关键词（be 动词、What's、have/has、can 等），
 * 找不到再挖最长的实义词。n 个空格。
 */
function pickBlanks(sentence, n, pattern = '') {
  const toks = tokenize(sentence);

  // 先尝试关键词规则
  for (const rule of KEYWORD_RULES) {
    if (!rule.test.test(pattern) && !rule.test.test(sentence)) continue;
    if (rule.findBe) {
      // There is/are... → 挖 be 动词
      for (let i = 0; i < toks.length; i++) {
        const clean = toks[i].replace(/[.!?,;:]/g, '');
        if (/^(is|are|am|was|were)$/i.test(clean) && i > 0 && /there/i.test(toks[i - 1].replace(/[.!?,;:]/g, ''))) {
          const result = [i];
          // 额外再挖一个实义词（名词/短语）
          const extras = toks.map((t, idx) => ({ t, idx }))
            .filter(({ t, idx }) => idx !== i && /[a-zA-Z]/.test(t) && !STOP_FILL.has(t.toLowerCase()) && !/^(is|are|am|was|were)$/i.test(t))
            .sort((a, b) => b.t.length - a.t.length)
            .slice(0, Math.max(0, n - 1));
          extras.forEach(e => result.push(e.idx));
          return [...new Set(result)].sort((a, b) => a - b).slice(0, n);
        }
      }
    } else if (rule.target) {
      for (let i = 0; i < toks.length; i++) {
        const clean = toks[i].replace(/[.!?,;:]/g, '');
        if (rule.target.test(clean)) {
          const result = [i];
          if (n > 1) {
            const extras = toks.map((t, idx) => ({ t, idx }))
              .filter(({ t, idx }) => idx !== i && /[a-zA-Z]/.test(t) && !STOP_FILL.has(t.toLowerCase()))
              .sort((a, b) => b.t.length - a.t.length)
              .slice(0, n - 1);
            extras.forEach(e => result.push(e.idx));
          }
          return [...new Set(result)].sort((a, b) => a - b).slice(0, n);
        }
      }
    }
  }

  // 降级：挖最长的实义词
  const cands = toks.map((t, i) => ({ t, i }))
    .filter(({ t }) => /[a-zA-Z]/.test(t) && !STOP_FILL.has(t.toLowerCase()))
    .sort((a, b) => b.t.replace(/[^a-zA-Z]/g, '').length - a.t.replace(/[^a-zA-Z]/g, '').length);
  const chosen = cands.slice(0, Math.min(n, Math.max(1, cands.length - 1)));
  return chosen.map(c => c.i).sort((a, b) => a - b);
}

function blankOut(sentence, idxs) {
  const toks = tokenize(sentence);
  const answers = idxs.map(i => toks[i]);
  idxs.forEach(i => { toks[i] = '____'; });
  return { text: toks.join(' ').replace(/\s+([.,!?;:])/g, '$1'), answers };
}

/**
 * 句型替换：尝试把句子做一次可控的小改写，产出「原句 → 改写」的对照。
 * 只做**规则明确**的几种，改写不出来就返回 null（不硬造）。
 */
export function makeTransformPair(sentence) {
  const en = String(sentence.en || '').trim();
  if (!en) return null;

  // There is a X. → There are two Xs.
  let m = en.match(/^There is an? ([a-zA-Z ]+?)\.?$/i);
  if (m) {
    const noun = m[1].trim();
    const plural = /(s|x|ch|sh)$/i.test(noun) ? `${noun}es` : `${noun}s`;
    return { from: en, to: `There are two ${plural}.`, hint: 'There is + 单数 → There are + 复数' };
  }
  // There are X. → There is a X.（取第一个名词）
  m = en.match(/^There are (?:two |three |some )?([a-zA-Z]+)s\.?$/i);
  if (m) return { from: en, to: `There is a ${m[1]}.`, hint: 'There are + 复数 → There is + 单数' };

  // I can X. → She can X.
  m = en.match(/^I can ([a-zA-Z ]+?)\.?$/i);
  if (m) return { from: en, to: `She can ${m[1]}.`, hint: 'can 后面动词不变，主语可以换' };

  // Let's X. → Let's X, too? 用建议句替换成疑问
  m = en.match(/^(Let's [a-zA-Z ]+?)\.?$/i);
  if (m) return { from: en, to: `${m[1]}, please.`, hint: "Let's 后面接动词原形" };

  // This is my X. → These are my Xs.
  m = en.match(/^This is my ([a-zA-Z ]+?)\.?$/i);
  if (m) {
    const noun = m[1].trim();
    const plural = /(s|x|ch|sh)$/i.test(noun) ? `${noun}es` : `${noun}s`;
    return { from: en, to: `These are my ${plural}.`, hint: 'This is → These are' };
  }

  return null;
}

const meta = (s, step, extra) => ({
  textbookId: s.textbookId,
  unitId: s.unitId,
  refType: 'sentence',
  refId: s.id,
  origin: 'generated',
  status: 'confirmed',
  step,
  level: STEP_LEVEL[step] || 3,
  ...extra,
});

/** 生成某句子的某一步题目 */
export function buildSentenceQuestion(s, stepKey, pool) {
  switch (stepKey) {
    case 'recall': {
      // 闪卡回忆：句子已隐藏，从记忆里补全整句（比「默写」轻一些：给首词和长度）
      const words = tokenize(s.en).filter(t => /[a-zA-Z]/.test(t));
      return makeQuestion(meta(s, stepKey, {
        type: 'fill',
        skill: 'S',
        prompt: `刚才那句话是什么？把它补完整（首词已给出）\n${words[0]} ${'____ '.repeat(Math.max(1, words.length - 1)).trim()}`,
        audioUrl: '',
        audio: s.en,
        answer: [s.en],
        accept: [s.en.replace(/[.!?]$/, '')],
        explain: `${s.en}\n${s.zh}`,
        hint: `共 ${words.length} 个单词，意思是「${s.zh}」`,
      }));
    }

    case 'fill': {
      const { text, answers } = blankOut(s.en, pickBlanks(s.en, 1, s.pattern || ""));
      return makeQuestion(meta(s, stepKey, {
        type: 'fill',
        skill: 'S',
        prompt: text,
        audioUrl: '',
        audio: s.en,
        answer: answers,
        explain: `${s.en}\n${s.zh}`,
        hint: `首字母 ${answers[0]?.[0] ?? ''}`,
      }));
    }

    case 'order': {
      const toks = tokenize(s.en);
      let mixed = shuffle(toks);
      if (mixed.join(' ') === toks.join(' ')) mixed = shuffle(toks);
      return makeQuestion(meta(s, stepKey, {
        type: 'order',
        skill: 'S',
        prompt: '把单词排成一句通顺的英文',
        audioUrl: '',
        audio: s.en,
        tokens: mixed,
        options: mixed,
        answer: [s.en],
        explain: `${s.en}\n${s.zh}`,
        hint: `第一个词：${toks[0]}`,
      }));
    }

    case 'translate':
      return makeQuestion(meta(s, stepKey, {
        type: 'transform',
        skill: 'S',
        prompt: `把中文翻译成英文：${s.zh}`,
        answer: [s.en],
        accept: [s.en.replace(/[.!?]$/, '')],
        explain: `${s.en}`,
        hint: s.pattern || `首词：${tokenize(s.en)[0]}`,
      }));

    case 'transform': {
      const pair = makeTransformPair(s);
      if (!pair) {
        // 这句没有可用的替换规则 → 换成「换个主语/开头」的开放改写，答案接受原句
        return makeQuestion(meta(s, stepKey, {
          type: 'transform',
          skill: 'S',
          prompt: `照样子改一改：${s.en}\n试着把句子里的人/物换掉，写一句新的英文。`,
          answer: [s.en],
          accept: [],
          explain: `参考：${s.en}\n只要能正确使用句型 ${s.pattern || ''} 就算对。`,
          hint: s.pattern || '',
          openEnded: true,
        }));
      }
      return makeQuestion(meta(s, stepKey, {
        type: 'transform',
        skill: 'S',
        prompt: `照样子改一改：\n${pair.from}\n→ ?`,
        answer: [pair.to],
        accept: [pair.to.replace(/[.!?]$/, '')],
        explain: `${pair.to}\n（${pair.hint}）`,
        hint: pair.hint,
      }));
    }

    case 'apply':
      return makeQuestion(meta(s, stepKey, {
        type: 'transform',
        skill: 'S',
        prompt: `用「${s.pattern || '这个句型'}」说一句你自己的话。\n（说得出来就大声读一遍）`,
        answer: [s.en],
        accept: [],
        explain: `参考答案：${s.en}\n${s.zh}`,
        hint: `可以照着这句改：${s.en}`,
        openEnded: true,
      }));

    default:
      return null;
  }
}

/** 句子流 */
export class SentenceStudyFlow {
  constructor(cfg) {
    this.id = uid('sflow');
    this.unitId = cfg.unitId || '';
    this.textbookId = cfg.textbookId || '';
    this.title = cfg.title || '句子学习';
    // 自定义勾选：先按 ids 过滤，再进入后续排序逻辑
    const picked = Array.isArray(cfg.ids) && cfg.ids.length
      ? (cfg.sentences || []).filter(s => cfg.ids.includes(s.id))
      : (cfg.sentences || []);
    this.pool = picked.length ? picked : (cfg.sentences || []);

    let order = cfg.priority?.size
      ? [...shuffle(this.pool).filter(s => cfg.priority.has(s.id)), ...shuffle(this.pool).filter(s => !cfg.priority.has(s.id))]
      : shuffle(this.pool);
    const limit = Math.min(cfg.limit || order.length, order.length);
    this.sentences = order.slice(0, Math.max(1, limit));

    this.queue = [];
    this.records = [];
    this.startedAt = nowISO();
    this.questionStartedAt = Date.now();
    this._hintUsed = false;
    this.streak = 0;

    // 第 1「轮」：每句先读懂、再闪卡记忆（这两步是同一段体验，连着做不打断）
    this.stage = 0;                      // 0=understand/recall，其后 fill→order→translate→transform→apply
    this.queue = this._planStage(0);
    this._applyAtEnd = false;
  }

  /** 每个阶段的步骤名（与 SENTENCE_STEPS 顺序一致，但把 understand+recall 合成一轮） */
  static STAGES = ['understand', 'fill', 'order', 'translate', 'transform', 'apply'];

  /**
   * 生成某一阶段的任务队列。
   * 阶段 0 是「读懂 + 闪卡」交替；其余阶段每句各一题，然后打散。
   */
  _planStage(stage) {
    const name = SentenceStudyFlow.STAGES[stage];
    if (!name) return [];
    if (name === 'understand') {
      const q = [];
      for (const s of shuffle(this.sentences)) {
        q.push({ sentenceId: s.id, step: 'understand' });
        q.push({ sentenceId: s.id, step: 'recall' });
      }
      return q;
    }
    return shuffle(this.sentences).map(s => ({ sentenceId: s.id, step: name }));
  }

  /** 当前阶段做完 → 进入下一阶段；没有下一阶段就结束 */
  _advanceSteps() {
    if (this.queue.length) return;
    this.stage += 1;
    this.queue = this._planStage(this.stage);
    // 阶段越界时队列为空，finished 自然为 true
  }

  get total() { return this.queue.length + this.records.length; }
  get done() { return this.records.length; }
  get current() { return this.queue[0] || null; }
  get finished() { return this.queue.length === 0; }
  get progress() { return this.total ? this.done / this.total : 0; }

  _sentenceOf(task) {
    return this.sentences.find(s => s.id === task.sentenceId);
  }

  currentQuestion() {
    const task = this.current;
    if (!task) return null;
    const s = this._sentenceOf(task);
    if (!s) return null;
    if (task.step === 'understand') {
      return { id: `und-${s.id}`, type: '__understand', sentence: s, step: 'understand', refType: 'sentence', refId: s.id };
    }
    return buildSentenceQuestion(s, task.step, this.pool);
  }

  /** 记忆闪卡的「先显示后隐藏」由 UI 控制；这里只前进 */
  ackUnderstand() {
    const task = this.current;
    if (!task || task.step !== 'understand') return null;
    this.queue.shift();
    this._advanceSteps();
    this.questionStartedAt = Date.now();
    return { ok: true };
  }

  /** 闪卡倒计时结束（无论是否作答都会调用，保证流程不断） */
  ackRecallShown() {
    // 仅用于 UI 提示，不改队列
  }

  useHint() { this._hintUsed = true; }

  submit(response) {
    const task = this.current;
    if (!task || task.step === 'understand') return null;
    const q = this.currentQuestion();
    const graded = grade(q, response);

    if (graded.correct) this.streak += 1; else this.streak = 0;

    const rec = {
      index: this.records.length,
      questionId: q.id,
      type: q.type,
      skill: 'S',
      level: q.level,
      refType: 'sentence',
      refId: task.sentenceId,
      unitId: this.unitId,
      prompt: q.prompt,
      expected: graded.expected,
      response: Array.isArray(response) ? response.join('|') : String(response ?? ''),
      correct: !!graded.correct,
      partial: graded.partial ?? (graded.correct ? 1 : 0),
      hintUsed: this._hintUsed,
      skipped: false,
      completion: graded.correct ? (this._hintUsed ? 'hinted' : 'independent') : 'failed',
      step: task.step,
      costMs: Date.now() - this.questionStartedAt,
      answeredAt: nowISO(),
    };
    this.records.push(rec);
    this.queue.shift();

    // 答错：把这一步排到队列尾部再来一次（允许重试，符合「答错给提示并允许重新尝试」）
    if (!graded.correct) {
      this.queue.push({ sentenceId: task.sentenceId, step: task.step, retry: true });
    }

    this._advanceSteps();
    this.questionStartedAt = Date.now();
    this._hintUsed = false;
    return graded;
  }

  /** 每句的完成情况（结束页展示「哪一句最吃力」） */
  sentenceProgress() {
    return this.sentences.map(s => {
      const rows = this.records.filter(r => r.refId === s.id);
      return {
        sentence: s,
        attempts: rows.length,
        correct: rows.filter(r => r.correct).length,
        wrongSteps: rows.filter(r => !r.correct).map(r => r.step),
      };
    });
  }

  summary() {
    const total = this.records.length;
    const correct = this.records.filter(r => r.correct).length;
    const independent = this.records.filter(r => r.completion === 'independent').length;
    const hinted = this.records.filter(r => r.completion === 'hinted').length;
    const failed = this.records.filter(r => r.completion === 'failed').length;
    const byLevel = {};
    for (const r of this.records) {
      const k = String(r.level ?? 0);
      byLevel[k] = byLevel[k] || { total: 0, correct: 0 };
      byLevel[k].total += 1;
      if (r.correct) byLevel[k].correct += 1;
    }
    return {
      id: this.id,
      module: 'sentence',
      title: this.title,
      unitId: this.unitId,
      textbookId: this.textbookId,
      total,
      correct,
      independent,
      hinted,
      failed,
      accuracy: total ? Math.round((correct / total) * 100) : 0,
      score: total ? Math.round((correct / total) * 100) : 0,
      bySkill: { S: { total, correct } },
      byLevel,
      startedAt: this.startedAt,
      endedAt: nowISO(),
      durationMs: Date.now() - new Date(this.startedAt).getTime(),
      records: this.records,
      sentencesTotal: this.sentences.length,
    };
  }
}
